import React from 'react';
import {
  Badge, Box, Card, CardMedia, CardContent, CardActions, Divider, Grid,
  IconButton, Typography,
} from '@mui/material';
import { FileDownloadOutlined as DownloadIcon, ContactPageOutlined as ContactPageIcon } from '@mui/icons-material';
import { useLazyQuery } from '@apollo/client';
import { PetPhoto } from '../../../types/PetPhoto';
import { downloadImage, formatRelativeTime } from '../../../utilities';
import { GET_PET_PHOTO } from '../../../queries';

interface PetCardProps {
  photo: PetPhoto;
  careType: string;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setUserId: React.Dispatch<React.SetStateAction<number | null>>;
  openMediaDialog: (img: string, date: string, name?: string) => void;
}

function PetCard({
  photo, careType, setDrawerOpen, setUserId, openMediaDialog,
}: PetCardProps) {
  const [getPetPhoto] = useLazyQuery(GET_PET_PHOTO, {
    onCompleted: (data) => {
      if (data?.petPhoto?.dataUrl) {
        const petName = photo.petName.trim();
        const date = `${new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)}`;
        const filename = `${date}_${petName ? `${petName.replace(/\s+/g, '_')}_` : ''}pet_photo.jpg`;
        downloadImage(data.petPhoto.dataUrl, filename);
      }
    },
  });

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const openPetParentProfile = (petParentId: string) => {
    setUserId(parseInt(petParentId, 10));
    setDrawerOpen(true);
  };

  const handleDownloadPhoto = (url: string) => {
    getPetPhoto({ variables: { url } });
  };

  return (
    <Grid item xs={12} sm={6} md={4} lg={3} key={photo.id}>
      <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ position: 'relative' }}>
          <Badge
            badgeContent={formatRelativeTime(photo.createdAt)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1,
              '& .MuiBadge-badge': {
                bgcolor: '#E5E7EB',
                color: '#374151',
                fontSize: '0.75rem',
                fontWeight: 500,
                minWidth: 'auto',
                padding: '4px 8px',
                borderRadius: '12px',
                position: 'static',
                transform: 'none',
              },
            }}
          />
          <CardMedia
            component="img"
            image={photo.url}
            alt={photo.petName?.trim() || 'Pet photo'}
            sx={{
              aspectRatio: '1/1', objectFit: 'cover', backgroundColor: '#eee', cursor: 'pointer',
            }}
            onClick={() => openMediaDialog(photo.url, photo.createdAt, photo.petName?.trim() ?? '')}
          />
        </Box>
        <CardContent>
          <Typography
            sx={{ fontSize: 16, fontWeight: 500 }}
            component="div"
            noWrap
          >
            {photo.petName?.trim() || '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {careType === 'Adopted' ? 'Adopted on' : 'Fostered on'}
            {' '}
            {formatDate(photo.exitDate)}
          </Typography>
        </CardContent>
        <Divider />
        <CardActions disableSpacing>
          <IconButton
            aria-label="download photo"
            onClick={() => handleDownloadPhoto(photo.url)}
            sx={{
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            <DownloadIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton
            aria-label="view pet parent profile"
            onClick={() => openPetParentProfile(photo.petParentId)}
            sx={{
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            <ContactPageIcon />
          </IconButton>
        </CardActions>
      </Card>
    </Grid>
  );
}

export default PetCard;
